import * as React from 'react';
import * as api from '../services/api-client';
import { NetworkError, ApiErrorResponse } from '../services/api-client';

/**
 * Domain state management for the Admin Console.
 * System_Admin sees all domains. Domain_Admin sees only assigned domains.
 * Implements optimistic updates with revert on failure.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Domain {
  readonly domainId: string;
  readonly domainName: string;
  readonly cloudflareZoneId: string;
  readonly credentialId: string;
  readonly createdAt: string;
  readonly createdBy: string;
}

export interface CreateDomainInput {
  readonly domainName: string;
  readonly cloudflareZoneId: string;
  readonly credentialId: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface DomainState {
  readonly domains: readonly Domain[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

export const initialDomainState: DomainState = {
  domains: [],
  isLoading: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type DomainAction =
  | { readonly type: 'DOMAINS_LOADING' }
  | { readonly type: 'DOMAINS_LOADED'; readonly payload: readonly Domain[] }
  | { readonly type: 'DOMAINS_ERROR'; readonly payload: string }
  | { readonly type: 'DOMAIN_CREATE_OPTIMISTIC'; readonly payload: Domain }
  | { readonly type: 'DOMAIN_CREATE_CONFIRMED' }
  | { readonly type: 'DOMAIN_CREATE_REVERT'; readonly payload: { readonly previousState: readonly Domain[] } };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function domainReducer(state: DomainState, action: DomainAction): DomainState {
  switch (action.type) {
    case 'DOMAINS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'DOMAINS_LOADED':
      return { domains: action.payload, isLoading: false, error: null };
    case 'DOMAINS_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    case 'DOMAIN_CREATE_OPTIMISTIC':
      return { ...state, domains: [...state.domains, action.payload], error: null };
    case 'DOMAIN_CREATE_CONFIRMED':
      return state;
    case 'DOMAIN_CREATE_REVERT':
      return { ...state, domains: action.payload.previousState, error: 'Failed to create domain. Reverted.' };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface DomainContextValue {
  readonly state: DomainState;
  readonly loadDomains: () => Promise<void>;
  readonly createDomain: (input: CreateDomainInput) => Promise<void>;
  readonly isConnecting: boolean;
}

const DomainContext = React.createContext<DomainContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface DomainProviderProps {
  readonly children: React.ReactNode;
}

/**
 * DomainProvider manages domain list state with optimistic updates.
 * Loads all domains for System_Admin, assigned domains for Domain_Admin.
 */
export function DomainProvider({ children }: DomainProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(domainReducer, initialDomainState);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const stateRef = React.useRef(state.domains);
  stateRef.current = state.domains;

  const loadDomains = React.useCallback(async (): Promise<void> => {
    dispatch({ type: 'DOMAINS_LOADING' });

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 3000];

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const data = await api.get<{ domains: readonly Domain[] }>('/admin/domains');
        dispatch({ type: 'DOMAINS_LOADED', payload: data.domains });
        setIsConnecting(false);
        return;
      } catch (err) {
        const isRetryable =
          err instanceof NetworkError ||
          (err instanceof ApiErrorResponse && err.status >= 500);

        if (isRetryable && attempt < MAX_RETRIES) {
          setIsConnecting(true);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
          continue;
        }

        setIsConnecting(false);
        dispatch({
          type: 'DOMAINS_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to load domains',
        });
        return;
      }
    }
  }, []);

  const createDomain = React.useCallback(async (input: CreateDomainInput): Promise<void> => {
    const previousState = stateRef.current;
    const optimistic: Domain = {
      domainId: crypto.randomUUID(),
      domainName: input.domainName,
      cloudflareZoneId: input.cloudflareZoneId,
      credentialId: input.credentialId,
      createdAt: new Date().toISOString(),
      createdBy: 'current-user',
    };

    dispatch({ type: 'DOMAIN_CREATE_OPTIMISTIC', payload: optimistic });
    try {
      await api.post('/admin/domains', input);
      dispatch({ type: 'DOMAIN_CREATE_CONFIRMED' });
      // Reload to get server-assigned IDs
      await loadDomains();
    } catch {
      dispatch({ type: 'DOMAIN_CREATE_REVERT', payload: { previousState } });
    }
  }, [loadDomains]);

  const contextValue = React.useMemo(
    (): DomainContextValue => ({
      state,
      loadDomains,
      createDomain,
      isConnecting,
    }),
    [state, loadDomains, createDomain, isConnecting],
  );

  return React.createElement(DomainContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access domain state and actions.
 * Must be used within a DomainProvider.
 */
export function useDomains(): DomainContextValue {
  const context = React.useContext(DomainContext);
  if (!context) {
    throw new Error('useDomains must be used within a DomainProvider');
  }
  return context;
}
